// M1 THROWAWAY (OPTI-6761): execute a SQL query against the prototype
// `/metrics/api/v1/query` endpoint and return the result rows shaped as
// `{ [column]: value }`. Mirrors the prototype `runQuery()` — React Query
// owns the cache. Delete once the proper agent lifecycle lands.

import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import axios from "axios";

import { useCurrentWorkspace } from "src/api/queries";

const METRICS_QUERY_PATH = "/metrics/api/v1/query";

type MetricsQueryRawResult = {
  columns?: string[];
  rows?: ReadonlyArray<ReadonlyArray<unknown>>;
  error?: string;
};

type MetricsQueryResponse = {
  results?: Record<string, MetricsQueryRawResult>;
};

export type MetricsQueryRow = Record<string, unknown>;

const fetchMetricsQuery = async (
  baseUrl: string,
  sql: string,
): Promise<MetricsQueryRow[]> => {
  const endpoint = `https://${baseUrl}${METRICS_QUERY_PATH}`;
  const { data } = await axios.post<MetricsQueryResponse>(endpoint, {
    queries: { q: sql },
  });
  const result = data.results?.q;
  if (!result) {
    throw new Error("No result returned from /metrics/api/v1/query");
  }
  if (result.error) {
    throw new Error(result.error);
  }
  const { columns, rows } = result;
  if (!columns || !rows) {
    throw new Error("Result missing columns or rows");
  }
  return rows.map((row) =>
    Object.fromEntries(columns.map((col, i) => [col, row[i]])),
  );
};

export const useMetricsQuery = (
  sql: string | undefined,
): UseQueryResult<MetricsQueryRow[], Error> => {
  const { data: workspace } = useCurrentWorkspace();
  const baseUrl = workspace?.base_url;

  return useQuery({
    queryKey: ["metrics-query", baseUrl, sql],
    queryFn: () => {
      if (!baseUrl) throw new Error("Workspace not loaded");
      if (!sql) throw new Error("No SQL provided");
      return fetchMetricsQuery(baseUrl, sql);
    },
    enabled: Boolean(baseUrl && sql),
    staleTime: Infinity,
  });
};
